/**
 * Copyright © 2026 Adnuntius AS.
 */
import {DOCUMENT, Inject, Injectable} from '@angular/core';
import * as _ from 'lodash';

import {AUTH_EVENT} from '../auth/auth-constants';
import {getRegisteredUrl} from '../../app/config/registered-urls';
import {ElectionResource} from '../api/resources/resource/election.resource';
import {NetworkInfoResource} from '../api/resources/resource/network.resource';
import {LocalNetworkInfo} from '../session/local-network-info';
import {LocalNetworkProfile} from '../session/local-network-profile';
import {LocalInactiveTargets} from '../session/local-inactive-targets';
import {LocalUserProfile} from '../session/local-user-profile';
import {AdnFormStateService} from './adn-form-state.service';
import {WorkspaceRegistryService} from '../workspace-registry.service';
import {WINDOW} from '../../app/angular-platform-providers';
import {AuthHttpBufferService} from '../auth/auth-http-buffer.service';

@Injectable({
  providedIn: 'root'
})
export class AdnNetworkHelperService {
  constructor(
    @Inject(WINDOW) private readonly window: any,
    @Inject(DOCUMENT) private readonly document: any,
    private readonly WorkspaceRegistry: WorkspaceRegistryService,
    private readonly LocalNetworkProfile: LocalNetworkProfile,
    @Inject(NetworkInfoResource) private readonly NetworkInfo: any,
    private readonly localUserProfile: LocalUserProfile,
    private readonly adnFormState: AdnFormStateService,
    @Inject(ElectionResource) private readonly Election: any,
    private readonly authHttpBuffer: AuthHttpBufferService
  ) {
  }

  switchNetwork(baseCtrl: any, eventBus: any, network: any): any {
    const previousNetwork = {
      networkId: this.LocalNetworkProfile.get('networkId'),
      tagId: this.LocalNetworkProfile.get('tagId'),
      defaultNetworkTagId: this.localUserProfile.get('defaultNetworkTagId')
    };

    this.setNetworkSwitchInProgress(true);

    this.localUserProfile.set('defaultNetworkTagId', network.tagId);
    this.LocalNetworkProfile.beginNetworkSwitch(network.tagId, network.id);
    LocalNetworkInfo.obtain().clearNetworkCache();
    LocalInactiveTargets.obtain().clear();

    const regUrl = getRegisteredUrl(this.window, network.id);
    LocalNetworkInfo.obtain().setSuppInfo('themeId', _.get(regUrl, ['id'], ''));

    const nInfoProm = this.NetworkInfo.get((data: any) => {
      LocalNetworkInfo.obtain().setInfo(data);
    }).$promise;

    const electionProm = this.Election.get({includeTests: true}, (data: any) => {
      LocalNetworkInfo.obtain().setSuppInfo('elections', data.results);
    }).$promise;

    return Promise.all([
      this.LocalNetworkProfile.refresh(),
      this.WorkspaceRegistry.sync(),
      nInfoProm,
      electionProm
    ]).then(() => {
      if (baseCtrl) {
        baseCtrl.currentNetwork = network;
      }
      eventBus.emit(AUTH_EVENT.networkChange, network);
      return new Promise<void>(resolve => {
        setTimeout(() => {
          this.reloadApp();
          resolve();
        }, 300);
      });
    }).catch(() => {
      return this.restorePreviousNetwork(previousNetwork).then(() => {
        const previousRegUrl = getRegisteredUrl(this.window, previousNetwork.networkId);
        LocalNetworkInfo.obtain().setSuppInfo('themeId', _.get(previousRegUrl, ['id'], ''));
        return Promise.all([
          this.NetworkInfo.get((data: any) => {
            LocalNetworkInfo.obtain().setInfo(data);
          }).$promise,
          this.Election.get({includeTests: true}, (data: any) => {
            LocalNetworkInfo.obtain().setSuppInfo('elections', data.results);
          }).$promise
        ]);
      }).finally(() => {
        this.setNetworkSwitchInProgress(false);
      });
    });
  }

  private setNetworkSwitchInProgress(inProgress: boolean): void {
    this.authHttpBuffer.networkSwitchInProgress = inProgress;
    const body = this.document.body;
    if (!body) {
      return;
    }
    if (inProgress) {
      body.classList.add('networkSwitching');
    } else {
      body.classList.remove('networkSwitching');
    }
  }

  private reloadApp(): void {
    this.adnFormState.deregister();
    this.window.location.reload();
  }

  private restorePreviousNetwork(previousNetwork: any): any {
    this.localUserProfile.set('defaultNetworkTagId', previousNetwork.defaultNetworkTagId);
    this.LocalNetworkProfile.beginNetworkSwitch(previousNetwork.tagId, previousNetwork.networkId);
    return this.LocalNetworkProfile.refresh();
  }
}
